import { useEffect, useMemo } from "react";
import {
    FireCMS,
    Scaffold,
    NavigationRoutes,
    SideDialogs,
    SnackbarProvider,
    ModeControllerProvider,
    useBuildModeController,
    useBuildNavigationController,
} from "@firecms/core";
import {
    FirebaseAuthController,
    FirebaseSignInProvider,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
} from "@firecms/firebase";
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { firebaseApp, auth } from "./firebase";

import { usersCollection } from "./collections/users";
import { purchasesCollection } from "./collections/purchases";
import { promoCodesCollection } from "./collections/promoCodes";
import { campaignsCollection } from "./collections/campaigns";
import { challengesCollection } from "./collections/challenges";
import { couponCampaignsCollection } from "./collections/couponCampaigns";
import { issuedCouponsCollection } from "./collections/couponNFTs";
import { eventsCollection } from "./collections/events";
import { badgeImagesCollection } from "./collections/badgeImages";
import { badgeNFTsCollection } from "./collections/badgeNFTs";
import { badgeDefinitionsCollection } from "./collections/badgeDefinitions";
import { AnalyticsDashboard } from "./views/AnalyticsDashboard";
import { ConfigurationView } from "./views/ConfigurationView";
import { HomeView } from "./views/HomeView";
import { CampaignWizard } from "./components/CampaignWizard";
import { NFTManagementView } from "./views/NFTManagementView";
import { BadgeDefinitionsView } from "./views/BadgeDefinitionsView";

// Allowed admin emails
const ADMIN_EMAILS = (import.meta.env.VITE_ADMIN_EMAILS || "")
    .split(",")
    .map((e: string) => e.trim().toLowerCase())
    .filter(Boolean);

export default function App() {
    const signInOptions: FirebaseSignInProvider[] = ["google.com"];

    const authController: FirebaseAuthController = useFirebaseAuthController({
        firebaseApp,
        signInOptions,
    });

    const firestoreDelegate = useFirestoreDelegate({ firebaseApp });
    const storageSource = useFirebaseStorageSource({ firebaseApp });
    const modeController = useBuildModeController();

    // Debug: Watch auth state at Firebase level (inside useEffect for HMR compatibility)
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            console.log("Firebase Auth State:", user ? { uid: user.uid, email: user.email } : "No user");
        }, (error) => {
            console.error("Firebase Auth Error:", error);
        });
        return () => unsubscribe();
    }, []);

    // Debugging: Log auth controller state
    useEffect(() => {
        console.log("Auth State Changed:", {
            initialLoading: authController.initialLoading,
            hasUser: !!authController.user,
            authError: authController.authError,
            uid: authController.user?.uid
        });
    }, [authController.initialLoading, authController.user, authController.authError]);

    const collections = useMemo(
        () => [
            usersCollection,
            purchasesCollection,
            promoCodesCollection,
            couponCampaignsCollection,
            issuedCouponsCollection,
            campaignsCollection,
            challengesCollection,
            eventsCollection,
            badgeImagesCollection,
            badgeNFTsCollection,
            badgeDefinitionsCollection,
        ],
        []
    );

    const views = useMemo(
        () => [
            {
                path: "dashboard",
                name: "Dashboard",
                icon: "Dashboard",
                description: "Platform overview",
                view: <HomeView />,
            },
            {
                path: "analytics",
                name: "Analytics",
                icon: "Analytics",
                description: "Platform analytics and metrics",
                view: <AnalyticsDashboard />,
            },
            {
                path: "configuration",
                name: "Configuration",
                icon: "Settings",
                description: "Pricing, tier limits, and feature settings",
                view: <ConfigurationView />,
            },
            {
                path: "create-campaign",
                name: "Create Campaign",
                icon: "AddCircle",
                group: "Promotions",
                description: "Step-by-step campaign creation wizard",
                view: <CampaignWizard />,
            },
            {
                path: "nft-management",
                name: "NFT Management",
                icon: "Token",
                group: "NFT",
                description: "Monitor and manage badge NFT minting, retry failures, and manage images",
                view: <NFTManagementView />,
            },
            {
                path: "badge-definitions",
                name: "Badge Definitions",
                icon: "EmojiEvents",
                group: "NFT",
                description: "Create, edit, and delete badge types via the API",
                view: <BadgeDefinitionsView />,
            },
        ],
        []
    );

    const navigationController = useBuildNavigationController({
        collections,
        views,
        authController,
        dataSourceDelegate: firestoreDelegate,
    });

    const handleLogin = async () => {
        try {
            console.log("Triggering Google Login (Popup)...", {
                authDomain: (firebaseApp.options as any).authDomain,
                currentOrigin: window.location.origin,
            });
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: 'select_account' });
            const result = await signInWithPopup(auth, provider);
            console.log("Login successful:", result.user.email);
        } catch (error: any) {
            console.error("Login Error:", {
                code: error.code,
                message: error.message,
                email: error.customData?.email,
                credential: GoogleAuthProvider.credentialFromError(error)
            });
            alert(`Login failed: ${error?.code} - ${error?.message}`);
        }
    };

    const isLoggedIn = authController.user !== null;
    const isAuthorized =
        isLoggedIn &&
        (ADMIN_EMAILS.length === 0 ||
            ADMIN_EMAILS.includes(authController.user?.email?.toLowerCase() || ""));

    return (
        <SnackbarProvider>
            <ModeControllerProvider value={modeController}>
                <FireCMS
                    navigationController={navigationController}
                    authController={authController}
                    dataSourceDelegate={firestoreDelegate}
                    storageSource={storageSource}
                    entityLinkBuilder={({ entity }) =>
                        `https://console.firebase.google.com/project/${import.meta.env.VITE_FIREBASE_PROJECT_ID}/firestore/data/${entity.path}/${entity.id}`
                    }
                >
                    {({ loading }) => {
                        const isAppLoading = loading || authController.initialLoading;

                        // Show terminal auth errors even during loading if they are critical
                        if (authController.authError && !isLoggedIn) {
                            return (
                                <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-4 text-center">
                                    <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center text-2xl mb-6 border border-red-500/20">
                                        ⚠️
                                    </div>
                                    <h1 className="text-2xl font-bold mb-2">Authentication Failure</h1>
                                    <p className="mb-8 text-slate-500 max-w-sm">
                                        {authController.authError.message || "We couldn't verify your credentials. Please try again or contact the system administrator."}
                                    </p>
                                    <button
                                        onClick={handleLogin}
                                        className="px-8 py-3 bg-red-600 hover:bg-red-700 rounded-2xl font-bold transition-all"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            );
                        }

                        // Safety check for missing config
                        if (isAppLoading && (
                            !import.meta.env.VITE_FIREBASE_API_KEY ||
                            import.meta.env.VITE_FIREBASE_API_KEY === "empty"
                        )) {
                            return (
                                <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-8 text-center">
                                    <div className="text-red-500 text-4xl mb-4">⚙️</div>
                                    <h2 className="text-xl font-bold mb-2">Configuration Missing</h2>
                                    <p className="text-slate-500">Firebase environment variables are not properly set. Check your .env file.</p>
                                </div>
                            );
                        }

                        if (isAppLoading) {
                            return (
                                <div className="flex flex-col items-center justify-center h-screen bg-black">
                                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                                    <div className="text-slate-500 font-medium tracking-widest text-xs uppercase">Initializing PapaqAI</div>
                                </div>
                            );
                        }

                        if (!isLoggedIn) {
                            return (
                                <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-4 text-center">
                                    <div className="mb-8 relative">
                                        <div className="w-24 h-24 bg-blue-600 rounded-3xl rotate-12 absolute -top-2 -left-2 opacity-20 blur-xl animate-pulse"></div>
                                        <div className="w-20 h-20 bg-blue-500 rounded-2xl flex items-center justify-center text-4xl shadow-2xl relative z-10">
                                            🎙️
                                        </div>
                                    </div>
                                    <h1 className="text-4xl font-black mb-2 tracking-tighter uppercase">PapaqAI Admin</h1>
                                    <p className="mb-10 text-slate-500 max-w-xs mx-auto">
                                        Advanced management portal for the PapaqAI language learning platform.
                                    </p>
                                    <button
                                        onClick={handleLogin}
                                        className="group relative px-8 py-4 bg-white text-black hover:bg-blue-50 rounded-2xl font-bold transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 shadow-xl"
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                        </svg>
                                        Sign in with Google
                                        <div className="absolute inset-0 rounded-2xl group-hover:blur-md bg-white -z-10 opacity-0 group-hover:opacity-40 transition-all"></div>
                                    </button>

                                    <div className="mt-12 pt-8 border-t border-slate-900 w-64">
                                        <div className="flex justify-center gap-4 text-slate-700 text-sm">
                                            <span>v2.0</span>
                                            <span>•</span>
                                            <span>System Ready</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        if (!isAuthorized) {
                            return (
                                <div className="flex flex-col items-center justify-center h-screen bg-black text-white p-4 text-center">
                                    <div className="w-20 h-20 bg-orange-500/10 text-orange-500 rounded-3xl flex items-center justify-center text-3xl mb-8 border border-orange-500/20">
                                        🚫
                                    </div>
                                    <h1 className="text-3xl font-black mb-2 tracking-tight">ACCESS DENIED</h1>
                                    <p className="mb-10 text-slate-500 max-w-sm mx-auto">
                                        The account <span className="text-white font-medium">{authController.user?.email}</span> is not on the whitelist for this portal.
                                    </p>
                                    <button
                                        onClick={() => authController.signOut()}
                                        className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 rounded-2xl font-bold transition-all"
                                    >
                                        Switch Account
                                    </button>
                                </div>
                            );
                        }

                        return (
                            <Scaffold>
                                <NavigationRoutes homePage={<HomeView />} />
                                <SideDialogs />
                            </Scaffold>
                        );
                    }}
                </FireCMS>
            </ModeControllerProvider>
        </SnackbarProvider>
    );
}
